/**
 * ABTestingTab - A/B variant management for campaigns (FULLY DYNAMIC)
 */
import { FlaskConical, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCampaignTheme, LEGACY_CAMPAIGN_NAMES } from './campaignUtils';

export default function ABTestingTab({
  report,
  allCampaigns,
  abVariants,
  loadingVariants,
  editingVariant,
  variantForm,
  onSetEditingVariant,
  onSetVariantForm,
  onSaveVariant,
  onRefreshVariants
}) {
  // Build dynamic campaign list from allCampaigns OR report.campaigns
  const campaignList = allCampaigns?.length > 0 
    ? allCampaigns 
    : report?.campaigns?.map(c => ({ campaign_id: c.campaign_id, title: c.title, theme: c.theme })) || [];

  if (loadingVariants) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* A/B Testing Header */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-4">
          <FlaskConical className="w-6 h-6 text-primary" />
          <div>
            <h2 className="font-heading text-xl text-foreground">Campaign A/B Testing</h2>
            <p className="text-sm text-muted-foreground">Create variant headlines and descriptions to test which performs better</p>
          </div>
        </div>
        
        <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
          <p><strong>How it works:</strong> Each campaign can have multiple variants (A, B, C, D). Visitors are randomly shown one variant, and we track which converts better.</p>
        </div>
      </div>

      {/* DYNAMIC Campaign Variant Cards */}
      {campaignList.map(campaignData => {
        const campaignId = campaignData.campaign_id;
        const theme = getCampaignTheme(campaignId, campaignData.theme);
        const variants = abVariants[campaignId] || [];
        const campaignName = campaignData.title || LEGACY_CAMPAIGN_NAMES[campaignId] || `Campaign ${campaignId}`;
        
        return (
          <div key={campaignId} className="bg-card rounded-2xl p-6 border border-border" data-testid={`ab-testing-campaign-${campaignId}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${theme.gradient} flex items-center justify-center text-white font-bold`}>
                  {campaignId}
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground">Campaign {campaignId}</h3>
                  <p className="text-sm text-muted-foreground truncate max-w-xs">{campaignName} - {variants.length} variant(s)</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  onSetEditingVariant(campaignId);
                  onSetVariantForm({ title: '', description: '', variant: variants.length === 0 ? 'A' : 'B' });
                }}
                className="rounded-full"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Variant
              </Button>
            </div>

            {/* Variant List */}
            {variants.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {variants.map((variant, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-xl border ${variant.is_default ? 'border-primary bg-primary/5' : 'border-border'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          variant.variant === 'A' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          variant.variant === 'B' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          variant.variant === 'C' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                          'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
                        }`}>
                          Variant {variant.variant}
                        </span>
                        {variant.is_default && (
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">Default</span>
                        )}
                        {variant.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <h4 className="font-medium text-foreground mb-1 line-clamp-2">{variant.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{variant.description?.slice(0, 100)}...</p>
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Views: </span>
                        <span className="font-bold text-foreground">{variant.views || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Signups: </span>
                        <span className="font-bold text-foreground">{variant.signups || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Conv: </span>
                        <span className={`font-bold ${
                          variant.views > 0 && (variant.signups / variant.views * 100) > 5 ? 'text-green-600' : 'text-foreground'
                        }`}>
                          {variant.views > 0 ? ((variant.signups / variant.views * 100) || 0).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FlaskConical className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No variants yet. Add your first variant to start A/B testing.</p>
              </div>
            )}

            {/* Add/Edit Variant Form */}
            {editingVariant === campaignId && (
              <div className="mt-6 p-4 bg-muted/50 rounded-xl border border-border">
                <h4 className="font-medium text-foreground mb-4">Add New Variant</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Variant Letter</Label>
                    <select
                      value={variantForm.variant}
                      onChange={(e) => onSetVariantForm(prev => ({ ...prev, variant: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 bg-card border border-border rounded-lg text-foreground"
                    >
                      <option value="A">A (Default)</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Title</Label>
                    <Input
                      value={variantForm.title}
                      onChange={(e) => onSetVariantForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter variant headline"
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm text-muted-foreground">Description</Label>
                    <textarea
                      value={variantForm.description}
                      onChange={(e) => onSetVariantForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter variant description/body text"
                      className="w-full mt-1 px-3 py-2 bg-card border border-border rounded-lg text-foreground min-h-[80px]"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={() => onSaveVariant(campaignId)}
                    className="rounded-full"
                  >
                    Save Variant
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      onSetEditingVariant(null);
                      onSetVariantForm({ title: '', description: '', variant: 'B' });
                    }}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state */}
      {campaignList.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No campaigns found. Create a campaign to start A/B testing.</p>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button 
          variant="outline" 
          onClick={onRefreshVariants}
          disabled={loadingVariants}
          className="rounded-full"
        >
          <FlaskConical className="w-4 h-4 mr-2" />
          Refresh Variants
        </Button>
      </div>
    </div>
  );
}
